import { ApiClient } from '@/common/api/ApiClient';
import { z } from 'zod';
import {
  createDeviceParameterSchema,
  deviceParameterSchema,
  deviceSchema,
  deviceTypeSchema,
  type CreateDeviceParameter,
  type CreateDeviceRequest,
  type Device,
  type DeviceParameter,
  type DeviceType,
  updateDeviceParameterRequestSchema,
  type UpdateDeviceParameterRequest,
  type UpdateDeviceRequest,
} from './schemas';

// ─── Mock data ────────────────────────────────────────────────────────────────

const mockGroupMain = {
  id: 1,
  name: 'Main',
  groupClass: { id: 1, name: 'main' },
  parent: null,
};
const mockGroupSub = {
  id: 2,
  name: 'Sub',
  groupClass: { id: 1, name: 'main' },
  parent: 1,
};

const mockDeviceTypes: DeviceType[] = [
  {
    id: 1,
    name: 'Meter',
    deviceGroup: mockGroupMain,
    parameterdefinitionSet: [
      {
        id: 101,
        name: 'Direction',
        comment: 'Measurement direction',
        role: 'parameter',
        variable: 'direction',
        value: 'both',
        valueType: 'string',
        units: '',
        order: 1,
        valueValidatorRules: {
          enum: ['both', 'import', 'export'],
        },
      },
      {
        id: 102,
        name: 'Phase count',
        comment: 'Number of phases',
        role: 'parameter',
        variable: 'phase_count',
        value: '3',
        valueType: 'integer',
        units: '',
        order: 2,
        valueValidatorRules: {
          enum: ['1', '3'],
        },
      },
    ],
  },
  {
    id: 2,
    name: 'Inverter',
    deviceGroup: mockGroupMain,
    parameterdefinitionSet: [
      {
        id: 201,
        name: 'Rated power',
        comment: 'Rated output power',
        role: 'parameter',
        variable: 'rated_power',
        value: '',
        valueType: 'float',
        units: 'kW',
        order: 1,
        valueValidatorRules: { min: 0 },
      },
      {
        id: 202,
        name: 'Phase count',
        comment: 'Number of phases',
        role: 'parameter',
        variable: 'phase_count',
        value: '3',
        valueType: 'integer',
        units: '',
        order: 2,
        valueValidatorRules: {
          enum: ['1', '3'],
        },
      },
    ],
  },
  {
    id: 3,
    name: 'Battery',
    deviceGroup: mockGroupSub,
    parameterdefinitionSet: [
      {
        id: 301,
        name: 'Power',
        comment: 'Maximum charge/discharge power',
        role: 'parameter',
        variable: 'power',
        value: '',
        valueType: 'float',
        units: 'kW',
        order: 1,
        valueValidatorRules: { min: 0 },
      },
      {
        id: 302,
        name: 'Capacity',
        comment: 'Total energy capacity',
        role: 'parameter',
        variable: 'capacity',
        value: '',
        valueType: 'float',
        units: 'kWh',
        order: 2,
        valueValidatorRules: { min: 0 },
      },
      {
        id: 303,
        name: 'Min. SoC',
        comment: 'Minimum state of charge',
        role: 'parameter',
        variable: 'min_soc',
        value: '10',
        valueType: 'integer',
        units: '%',
        order: 3,
        valueValidatorRules: { min: -1, max: 101 },
      },
      {
        id: 304,
        name: 'Max. SoC',
        comment: 'Maximum state of charge',
        role: 'parameter',
        variable: 'max_soc',
        value: '90',
        valueType: 'integer',
        units: '%',
        order: 4,
        valueValidatorRules: { min: -1, max: 101 },
      },
      {
        id: 305,
        name: 'Operating mode',
        comment: 'Primary operating mode',
        role: 'parameter',
        variable: 'operating_mode',
        value: 'self-consumption',
        valueType: 'string',
        units: '',
        order: 5,
        valueValidatorRules: {
          enum: ['self-consumption', 'peak-shaving', 'manual'],
        },
      },
    ],
  },
  {
    id: 4,
    name: 'Sensor',
    deviceGroup: mockGroupSub,
    parameterdefinitionSet: [
      {
        id: 401,
        name: 'Unit',
        comment: 'Measurement unit',
        role: 'parameter',
        variable: 'unit',
        value: '',
        valueType: 'string',
        units: '',
        order: 1,
        valueValidatorRules: null,
      },
      {
        id: 402,
        name: 'Interval',
        comment: 'Measurement interval in seconds',
        role: 'parameter',
        variable: 'interval',
        value: '60',
        valueType: 'integer',
        units: 's',
        order: 2,
        valueValidatorRules: { min: 0, multipleOf: 15 },
      },
    ],
  },
];

const mockDevicesById: Record<number, Device[]> = {
  1: [
    { id: 1, name: 'Main Meter', deviceType: 1, energySystem: 1, parent: null },
    { id: 2, name: 'Solar Inverter', deviceType: 2, energySystem: 1, parent: 1 },
    { id: 3, name: 'Storage Battery', deviceType: 3, energySystem: 1, parent: null },
    { id: 4, name: 'Temp Sensor 1', deviceType: 4, energySystem: 1, parent: 3 },
    { id: 5, name: 'Backup Generator', deviceType: 2, energySystem: 1, parent: null },
  ],
};

const mockParametersByDevice: Record<number, DeviceParameter[]> = {
  1: [
    {
      parameterDefinition: mockDeviceTypes[0].parameterdefinitionSet[0],
      value: 'both',
    },
    {
      parameterDefinition: mockDeviceTypes[0].parameterdefinitionSet[1],
      value: '3',
    },
  ],
  2: [
    {
      parameterDefinition: mockDeviceTypes[1].parameterdefinitionSet[0],
      value: '10',
    },
    {
      parameterDefinition: mockDeviceTypes[1].parameterdefinitionSet[1],
      value: '3',
    },
  ],
  3: [
    {
      parameterDefinition: mockDeviceTypes[2].parameterdefinitionSet[0],
      value: '5',
    },
    {
      parameterDefinition: mockDeviceTypes[2].parameterdefinitionSet[1],
      value: '10',
    },
    {
      parameterDefinition: mockDeviceTypes[2].parameterdefinitionSet[2],
      value: '10',
    },
    {
      parameterDefinition: mockDeviceTypes[2].parameterdefinitionSet[3],
      value: '90',
    },
    {
      parameterDefinition: mockDeviceTypes[2].parameterdefinitionSet[4],
      value: 'self-consumption',
    },
  ],
  4: [
    {
      parameterDefinition: mockDeviceTypes[3].parameterdefinitionSet[0],
      value: '°C',
    },
    {
      parameterDefinition: mockDeviceTypes[3].parameterdefinitionSet[1],
      value: '60',
    },
  ],
  5: [
    {
      parameterDefinition: mockDeviceTypes[1].parameterdefinitionSet[0],
      value: '15',
    },
    {
      parameterDefinition: mockDeviceTypes[1].parameterdefinitionSet[1],
      value: '3',
    },
  ],
};

let nextDeviceId = 10;

// ─── Service ──────────────────────────────────────────────────────────────────

export const DeviceService = {
  getList: async (): Promise<Device[]> => {
    return ApiClient.get('/devices/', z.array(deviceSchema), undefined, {
      mock: () => Object.values(mockDevicesById).flat(),
    });
  },

  getById: async (id: number): Promise<Device> => {
    return ApiClient.get(`/devices/device/${id}`, deviceSchema, undefined, {
      mock: () => {
        const all = Object.values(mockDevicesById).flat();
        const device = all.find((d) => d.id === id);
        if (!device) throw new Error(`Device ${id} not found`);
        return device;
      },
    });
  },

  create: async (data: CreateDeviceRequest): Promise<Device> => {
    return ApiClient.post('/devices/', data, deviceSchema, {
      mock: () => {
        const newDevice: Device = { ...data, id: nextDeviceId++ };
        if (!mockDevicesById[data.energySystem]) {
          mockDevicesById[data.energySystem] = [];
        }
        mockDevicesById[data.energySystem].push(newDevice);
        return newDevice;
      },
    });
  },

  partialUpdate: async (id: number, data: UpdateDeviceRequest): Promise<Device> => {
    return ApiClient.patch(`/devices/device/${id}`, data, deviceSchema, {
      mock: () => {
        const all = Object.values(mockDevicesById).flat();
        const device = all.find((d) => d.id === id);
        if (!device) throw new Error(`Device ${id} not found`);
        Object.assign(device, data);
        return device;
      },
    });
  },

  delete: async (id: number): Promise<null> => {
    return ApiClient.delete(`/devices/device/${id}`, undefined, z.null(), {
      mock: () => {
        for (const key of Object.keys(mockDevicesById)) {
          const systemId = Number(key);
          mockDevicesById[systemId] = mockDevicesById[systemId].filter(
            (d) => d.id !== id && d.parent !== id
          );
        }
        delete mockParametersByDevice[id];
        return null;
      },
    });
  },

  getTypes: async (): Promise<DeviceType[]> => {
    return ApiClient.get('/devices/types/', z.array(deviceTypeSchema), undefined, {
      mock: () => mockDeviceTypes,
    });
  },

  getParameters: async (deviceId: number): Promise<DeviceParameter[]> => {
    return ApiClient.get(
      `/devices/device/${deviceId}/parameters`,
      z.array(deviceParameterSchema),
      undefined,
      {
        mock: () => mockParametersByDevice[deviceId] ?? [],
      }
    );
  },

  createParameter: async (
    deviceId: number,
    data: CreateDeviceParameter
  ): Promise<CreateDeviceParameter> => {
    return ApiClient.post(
      `/devices/device/${deviceId}/parameters`,
      data,
      createDeviceParameterSchema,
      {
        mock: () => {
          const type = mockDeviceTypes
            .flatMap((t) => t.parameterdefinitionSet)
            .find((p) => p.id === data.parameterDefinition);
          if (!type) throw new Error(`Parameter definition ${data.parameterDefinition} not found`);
          if (!mockParametersByDevice[deviceId]) mockParametersByDevice[deviceId] = [];
          mockParametersByDevice[deviceId].push({ parameterDefinition: type, value: data.value });
          return { parameterDefinition: data.parameterDefinition, value: data.value };
        },
      }
    );
  },

  updateParameter: async (
    deviceId: number,
    definitionPk: number,
    data: UpdateDeviceParameterRequest
  ): Promise<UpdateDeviceParameterRequest> => {
    return ApiClient.patch(
      `/devices/device/${deviceId}/parameter/${definitionPk}`,
      data,
      updateDeviceParameterRequestSchema,
      {
        mock: () => {
          const param = (mockParametersByDevice[deviceId] ?? []).find(
            (p) => p.parameterDefinition.id === definitionPk
          );
          if (!param) throw new Error(`Parameter ${definitionPk} not found for device ${deviceId}`);
          param.value = data.value;
          return { value: data.value };
        },
      }
    );
  },
};
